import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import MasterUserTenantsSection, {
  draftsToTenantInputs,
  mapTenantsToDrafts,
  type MasterUserTenantDraft,
  validateTenantDrafts,
} from '../../components/sistema/MasterUserTenantsSection';
import masterUserAdminService from '../../services/masterUserAdminService';
import tenantAdminService from '../../services/tenantAdminService';
import { TenantAdmin } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';

const USERNAME_PATTERN = /^[A-Za-z0-9._@-]+$/;

interface MasterUserFormState {
  username: string;
  displayName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const defaultForm: MasterUserFormState = {
  username: '',
  displayName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

function validateForm(
  form: MasterUserFormState,
  isCreate: boolean,
): string | null {
  const username = form.username.trim();
  const displayName = form.displayName.trim();
  const email = form.email.trim();
  const password = form.password;
  const confirmPassword = form.confirmPassword;

  if (isCreate) {
    if (!username) return 'El usuario es obligatorio';
    if (username.length > 100) return 'El usuario no puede superar 100 caracteres';
    if (/\s/.test(username)) return 'El usuario no puede contener espacios';
    if (!USERNAME_PATTERN.test(username)) {
      return 'El usuario solo puede contener letras, números y los caracteres . _ @ -';
    }
    if (!password) return 'La contraseña es obligatoria';
  }

  if (displayName.length > 200) return 'El nombre no puede superar 200 caracteres';
  if (email && email.length > 256) return 'El email no puede superar 256 caracteres';
  if (email && !email.includes('@')) return 'El email no es válido';

  if (password || confirmPassword) {
    if (password !== confirmPassword) return 'Las contraseñas no coinciden';
    if (password.length < 6) return 'La contraseña debe tener al menos 6 caracteres';
  }

  return null;
}

const MasterUserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<MasterUserFormState>({ ...defaultForm });
  const [tenantRows, setTenantRows] = useState<MasterUserTenantDraft[]>([]);
  const [tenants, setTenants] = useState<TenantAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const isCreate = id === 'nuevo' || !id;
  const userId = useMemo(() => {
    if (isCreate || !id) return null;
    const parsed = parseInt(id, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [id, isCreate]);

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const data = await tenantAdminService.listPaged({
          pageNumber: 1,
          pageSize: 100,
        });
        setTenants(data.items);
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar tenants'), {
          variant: 'error',
          preventDuplicate: true,
        });
      }
    };
    void loadTenants();
  }, [enqueueSnackbar]);

  const loadUser = useCallback(async () => {
    if (userId == null) return;
    try {
      setLoading(true);
      const user = await masterUserAdminService.getById(userId);
      setFormData({
        username: user.username,
        displayName: user.displayName ?? '',
        email: user.email ?? '',
        password: '',
        confirmPassword: '',
      });
      setTenantRows(
        user.tenants?.length
          ? mapTenantsToDrafts(user.tenants)
          : [],
      );
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el usuario'), { variant: 'error' });
      navigate('/sistema/usuarios-maestros');
    } finally {
      setLoading(false);
    }
  }, [userId, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!isCreate && userId != null) {
      loadUser();
    }
  }, [isCreate, userId, loadUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm(formData, isCreate);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'warning' });
      return;
    }

    const tenantValidationError = validateTenantDrafts(tenantRows);
    if (tenantValidationError) {
      enqueueSnackbar(tenantValidationError, { variant: 'warning' });
      return;
    }

    const tenantAssignments = draftsToTenantInputs(tenantRows);

    try {
      setSaving(true);
      if (isCreate) {
        await masterUserAdminService.create({
          username: formData.username.trim(),
          displayName: formData.displayName.trim() || undefined,
          email: formData.email.trim() || undefined,
          password: formData.password,
          tenants: tenantAssignments,
        });
        enqueueSnackbar('Usuario creado correctamente', { variant: 'success' });
      } else if (userId != null) {
        await masterUserAdminService.update(userId, {
          displayName: formData.displayName.trim() || undefined,
          email: formData.email.trim() || undefined,
          password: formData.password || undefined,
        });
        await masterUserAdminService.setUserTenants(userId, tenantAssignments);
        enqueueSnackbar('Usuario actualizado correctamente', { variant: 'success' });
      }
      navigate('/sistema/usuarios-maestros');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar el usuario'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!isCreate && userId == null) {
    return <Navigate to="/sistema/usuarios-maestros" replace />;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/usuarios-maestros')}
              sx={{ textTransform: 'none' }}
            >
              Usuarios maestros
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isCreate ? 'Nuevo usuario maestro' : 'Editar usuario maestro'}
            </Typography>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Tabs
                value={currentTab}
                onChange={(_, value) => setCurrentTab(value)}
                sx={{ mb: 3 }}
              >
                <Tab label="General" />
                <Tab label="Tenants" />
              </Tabs>

              <Stack spacing={3}>
                {currentTab === 0 && (
                  <Stack spacing={3} maxWidth={480}>
                    <TextField
                      name="username"
                      label="Usuario"
                      value={formData.username}
                      onChange={handleChange}
                      required={isCreate}
                      fullWidth
                      disabled={saving || !isCreate}
                      helperText={
                        isCreate
                          ? 'Identificador de login (sin espacios)'
                          : 'El usuario no se puede modificar'
                      }
                      inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                      name="displayName"
                      label="Nombre para mostrar"
                      value={formData.displayName}
                      onChange={handleChange}
                      fullWidth
                      disabled={saving}
                      inputProps={{ maxLength: 200 }}
                    />
                    <TextField
                      name="email"
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                      disabled={saving}
                      inputProps={{ maxLength: 256 }}
                    />
                    <TextField
                      name="password"
                      label={isCreate ? 'Contraseña' : 'Nueva contraseña'}
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={isCreate}
                      fullWidth
                      disabled={saving}
                      helperText={isCreate ? undefined : 'Dejar en blanco para no cambiar'}
                    />
                    <TextField
                      name="confirmPassword"
                      label="Confirmar contraseña"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required={isCreate}
                      fullWidth
                      disabled={saving}
                    />
                  </Stack>
                )}

                {currentTab === 1 && (
                  <MasterUserTenantsSection
                    tenants={tenants}
                    rows={tenantRows}
                    disabled={saving}
                    onChange={setTenantRows}
                  />
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={saving}
                    onClick={() => navigate('/sistema/usuarios-maestros')}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" variant="contained" color="primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MasterUserForm;
